<template>
  <div
    :id="uid"
    class="tw-flex tw-content-start tw-items-center"
    @click="select"
  >
    <div
      class="radioWrapper"
      :class="{ selected: innerValue, error: !validity }"
    >
      <transition name="fade">
        <div v-if="innerValue" class="innerCircle"></div>
      </transition>
    </div>
    <div v-if="$slots.label" class="label">
      <slot name="label" />
    </div>
    <div v-else-if="label.length" class="label">{{ label }}</div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import validationChildMixin from '~/mixins/validationChild'

import type { PropType } from 'vue'

export default defineComponent({
  mixins: [validationChildMixin],
  props: {
    label: {
      type: String,
      required: false,
      default(): string {
        return ''
      },
    },
    modelValue: {
      type: [Array, String, Boolean, Number] as PropType<
        string[] | number[] | string | boolean | number
      >,
      required: true,
    },
    valueName: {
      type: [String, Number] as PropType<string | number>,
      required: false,
      default(): number {
        return 0
      },
    },
    inverted: {
      type: Boolean,
      required: false,
      default(): boolean {
        return false
      },
    },
    disabled: {
      type: Boolean,
      required: false,
      default(): boolean {
        return false
      },
    },
    picto: {
      type: String,
      default(): string {
        return 'fas fa-check'
      },
    },
  },
  emits: ['update:modelValue', 'change'],
  setup() {
    const uid = useId()

    return {
      uid,
    }
  },
  computed: {
    pictoClass(): Record<string, boolean> {
      return this.picto.split(' ').reduce(
        (accumulator: Record<string, boolean>, className: string) => {
          accumulator[className] = true
          return accumulator
        },
        {} as Record<string, boolean>,
      )
    },
    innerValue(): boolean {
      if (typeof this.modelValue === 'boolean') {
        return this.inverted ? !this.modelValue : this.modelValue
      } else if (['string', 'number'].includes(typeof this.modelValue)) {
        return this.inverted
          ? !(this.modelValue === this.valueName)
          : this.modelValue === this.valueName
      } else if (typeof this.modelValue === 'object') {
        if (this.inverted)
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore Can't make it work
          return !this.modelValue.includes(this.valueName)
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore Can't make it work
        else return this.modelValue.includes(this.valueName)
      } else {
        return false
      }
    },
  },
  methods: {
    select(): void {
      if (!this.disabled) {
        this.$emit('change')
        if (typeof this.modelValue === 'boolean')
          this.$emit('update:modelValue', true)
        else if (
          typeof this.modelValue === 'string' ||
          typeof this.modelValue === 'number'
        )
          this.$emit('update:modelValue', this.valueName)
        else if (typeof this.modelValue === 'object')
          this.$emit('update:modelValue', [this.valueName])
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.radioWrapper {
  @apply tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-gray-400 tw-p-1 tw-transition tw-duration-150 tw-ease-in;
}

.parentRadioHover .radioWrapper {
  @apply tw-transition-colors tw-duration-150 tw-ease-in-out;
}

.parentRadioHover:hover .radioWrapper:not(.selected) {
  @apply tw-border-black;
}
.error.radioWrapper {
  @apply tw-border-error;
}
.radioWrapper.selected {
  @apply tw-border-orange-500 tw-duration-100 tw-ease-out;
}
.innerCircle {
  @apply tw-h-full tw-w-full tw-rounded-full tw-bg-orange-500;
}
.label {
  @apply tw-ml-3 tw-cursor-pointer tw-text-base tw-text-black;
}
</style>
